import { setClearControllerSlice } from "../redux/slices/controllerSlice";
import { setClearVideoDataSlice, setErrorType, setIsFetchDetails, setIsValidAddress, setStreamLinkParams } from "../redux/slices/videoDataSlice";
import { livePoll } from "./constants";
import store from "../redux/store";
import { conditions } from "./conditions/conditions";
import { ConditionName, VideoNumberTypeEnum } from "../interfaces/enums";
import { apiCaller } from "../services/apiCaller";
import { API } from "../services/services";
import { setClearPopoversSlice } from "../redux/slices/popoversSlice";
import { setClearUserSlice } from "../redux/slices/userSlice";
import { setClearTagsSlice } from "../redux/slices/tagsSlice";
import { setClearCommentsSlice } from "../redux/slices/commentsSlice";
import { setClearChatSlice } from "../redux/slices/chatSlice";
import { whiteListGeneralUrl, whiteListTargets } from "./environment";
import { authToken, findFirstVideoInRecommendationList } from "redux-share";

export const wait = (s: number) => new Promise((resolve) => setTimeout(resolve, s * 1000));

export const getRemainingTime = (timestamp: number) => {
    const timePassedInSeconds = Math.floor(Date.now() / 1000 - timestamp);
    const rtf = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
    switch (true) {
        case timePassedInSeconds < 60:
            return rtf.format(-timePassedInSeconds, "second");
        case timePassedInSeconds < 3600:
            return rtf.format(-Math.floor(timePassedInSeconds / 60), "minute");
        case timePassedInSeconds < 86400:
            return rtf.format(-Math.floor(timePassedInSeconds / 3600), "hour");
        case timePassedInSeconds < 2592000:
            return rtf.format(-Math.floor(timePassedInSeconds / 86400), "day");
        case timePassedInSeconds < 31536000:
            return rtf.format(-Math.floor(timePassedInSeconds / 2592000), "month");
        default:
            return rtf.format(-Math.floor(timePassedInSeconds / 31536000), "year");
    }
};

export const compare = (a: any, b: any) => {
    if (a.time < b.time) return -1;
    if (a.time > b.time) return 1;
    return 0;
};

export const handlePageTitle = (title: string) => {
    if (title && conditions(ConditionName.showModuleTitle)) document.title = title;
};

export const getUUID = () => {
    let uuid = localStorage.getItem("hamrahiUUID");
    if (!uuid) {
        uuid = uuidGenerator();
        localStorage.setItem("hamrahiUUID", uuid);
    }
    return uuid.toString();
};

export const uuidGenerator = () => {
    let uuid = "xxxxxxxx-xxxx-Mxxx-Nxxx-x".replace(/[xMN]/g, (char) => {
        let val: number;
        if (char === "M") val = Math.floor(1 + Math.random() * 5);
        else if (char === "N") val = Math.floor(8 + Math.random() * 4);
        else val = Math.floor(Math.random() * 16);
        return val.toString(16);
    });
    uuid += Date.now().toString(16);
    return uuid;
};

export const customizeInterval = async (timeCount: number, cb: Function) => {
    await cb();
    await wait(timeCount);
    customizeInterval(timeCount, cb);
};

export const isTouchScreen = () =>
    "ontouchstart" in window || ((window as any)["DocumentTouch"] && document instanceof (window as any)["DocumentTouch"]);
export const isTouchScreenLandscape = () => {
    const { videoElementSizeInfo } = store.getState().videoData;
    const { isFullScreen } = store.getState().controller;
    if (conditions(ConditionName.isMiniPlayer) && videoElementSizeInfo.width !== 0) {
        return isTouchScreen() && videoElementSizeInfo.width > videoElementSizeInfo.height;
    }
    return isTouchScreen() && isFullScreen;
};
export const isTouchScreenPortrait = () => {
    const { videoElementSizeInfo } = store.getState().videoData;
    const { isFullScreen } = store.getState().controller;
    // if (conditions(ConditionName.isMiniPlayer) && videoElementSizeInfo.width !== 0) {
    //     return (isTouchScreen() && videoElementSizeInfo.width <= videoElementSizeInfo.height);
    // }
    return isTouchScreen() && !isFullScreen;
};
export const handleIsComplexDesktop = (isPortrait: boolean) => !isTouchScreen() && !document.fullscreenElement && isPortrait;

export const isIOS = () => {
    return (
        ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
};

export const controllbarIconSize = {
    miniPlayer: 15,
    miniPlayerFontAwesome: "pl-text-[15px]",
    other: 20,
    otherFontAwesome: "pl-text-[22px]"
};

export const addZeroBefore = (n: number) => {
    return (n < 10 ? "0" : "") + n;
};

export const secondsToHMS = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);
    return h < 1 ? `${addZeroBefore(m)}:${addZeroBefore(s)}` : `${addZeroBefore(h)}:${addZeroBefore(m)}:${addZeroBefore(s)}`;
};

export const secondsToPersianHMS = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);
    const second = s ? `${s} ثانیه` : null;
    const minute = m ? `${m} دقیقه` : null;
    const hour = h ? `${h} ساعت` : null;
    return [hour, minute, second].filter((item) => item !== null);
};

export const efficientSecondsToPersianHMS = (seconds: number) => {
    const h = seconds >= 60 * 60 ? Math.floor(seconds / 3600) : 0;
    const m = seconds >= 60 ? Math.floor((seconds % 3600) / 60) : 0;
    const s = seconds < 60 * 60 ? Math.floor((seconds % 3600) % 60) : 0;
    const second = s ? `${s} ثانیه` : null;
    const minute = m ? `${m} دقیقه` : null;
    const hour = h ? `${h} ساعت` : null;
    return [hour, minute, second].filter((item) => item !== null).join(" و ");
};

export const staticFormats = (realFormat: string) => {
    if (["mp4", "mkv", "mov", "m4v"].includes(realFormat)) return "mp4";
    else if (realFormat === "webm" || realFormat === "flv") return realFormat;
    else if (["mp3", "ogg"].includes(realFormat)) return "mp3";
    else return false;
};

export const addUuidToSrc = (src: string) => {
    const urlObject = new URL(src);
    let urlSrc = src;
    if (urlObject.search.length) return (urlSrc += `&uuid=${getUUID()}`);
    else return (urlSrc += `?uuid=${getUUID()}`);
};

export const handleSumVotes = (question: any) => {
    let sumVotes = 0;
    if (question && Object.keys(question).length > 0 && question.options.length > 0) {
        question.options.forEach((option: any) => {
            if (
                livePoll() &&
                livePoll().polls.filter((poll: any) => poll.qid === question.id && poll.oid === option.id && poll.sumOptionVotes === option.vote + 1)
                    .length > 0
            )
                sumVotes += 1;
            sumVotes += option.vote;
        });
    }
    return sumVotes;
};

export const checkParentTarget = (event: any, target: string) => {
    try {
        return [
            event.target.id,
            event.target.parentNode.id,
            event.target.parentNode.parentNode.id,
            event.target.parentNode.parentNode.parentNode.id,
            event.target.parentNode.parentNode.parentNode.parentNode.id
        ].includes(target);
    } catch (error) {
        return false;
    }
};

export const unitization = (viewers: number) => {
    if (viewers / 1000 < 1) {
        return { value: viewers, unit: "" };
    } else if (viewers / (1000 * 1000) < 1) {
        return { value: Math.ceil((viewers / 1000) * 10) / 10, unit: "K" };
    } else if (viewers / (1000 * 1000 * 1000) < 1) {
        return { value: Math.ceil((viewers / (1000 * 1000)) * 10) / 10, unit: "M" };
    } else return { value: 0, unit: "" };
};

export const handleNotActiveText = (errorType: string = "404") => {
    if (errorType === "liveAndVod") {
        return {
            title: "در حال حاضر محتوای ویدیویی در این کانال  برای این پخش‌زنده وجود ندارد.",
            description: "شما می‌توانید سایر پخش زنده‌های ما  را مشاهده کنید یا برنامه پخش زنده‌های آینده کانال مورد نظر خود را چک کنید."
        };
    } else if (errorType === "404" || errorType === undefined) {
        return {
            title: "404",
            description: "آدرس وارد شده صحیح نیست."
        };
    }
};

export const isVoted = (qid: number) => livePoll() && livePoll().polls.some((poll: any) => poll.qid === qid);
export const isSelectedOption = (qid: number, oid: number) =>
    livePoll() && livePoll().polls.some((poll: any) => poll.qid === qid && poll.oid === oid);

export const appendQueryParamsToChunks = (link: string) => {
    const dispatch = store.dispatch;
    const { token, params } = store.getState().videoData;
    let queryParams: string = "";
    queryParams = link.split("m3u8")[1];
    if (queryParams) queryParams = `${queryParams}&uuid=${getUUID()}`;
    else queryParams = `?uuid=${getUUID()}`;

    if (conditions(ConditionName.isAuth)) {
        if (hasQuestionMark(queryParams)) queryParams = `${queryParams}&token=${token}`;
        else queryParams = `?token=${token}`;
    }
    dispatch(setStreamLinkParams(queryParams));
};

export const inViewPort = (element: any) => {
    if (element) {
        const positionLeft = element.offsetLeft - element.offsetWidth / 2;
        const positionRight = window.innerWidth - (element.offsetLeft + element.offsetWidth);
        if (positionLeft >= 0 && positionRight >= 0) return "inViewport";
        else if (positionLeft < 0) return "OutViewPortFromLeft";
        else if (positionRight < 0) return "OutViewPortFromRight";
    }
};

export const handleIsComplexPortrait = (params?: any) => {
    return params.portrait === "true" && isTouchScreenPortrait() && params.isGeneral !== "true" && params.raw !== "true";
};
export const isFullMobilePortrait = () => {
    const { params, videoElementSizeInfo } = store.getState().videoData;
    return (
        params.portrait !== "true" &&
        (+videoElementSizeInfo.width.toFixed(0) === window.screen.width || +videoElementSizeInfo.width.toFixed(0) === window.screen.availWidth) &&
        (window.screen.height > window.screen.width || window.screen.availHeight > window.screen.availWidth)
    );
};

export const handleRippleCircle = (event: any, id: string) => {
    const element: any = document.getElementById(id);
    const circle = document.createElement("span");
    circle.style.width = circle.style.height = `50px`;
    circle.style.left = `${event.touches[0].clientX - element.offsetLeft - 25}px`;
    circle.style.top = `${event.touches[0].clientY - element.offsetTop - 25}px`;
    circle.classList.add("pl-ripple");
    const ripple = element.getElementsByClassName("pl-ripple")[0];
    if (ripple) ripple.remove();
    element.appendChild(circle);
};

export const handleRipple = (event: any, id: string, size: number = 50) => {
    const clientX = event.clientX !== undefined ? event.clientX : event.touches[0].clientX;
    const clientY = event.clientY !== undefined ? event.clientY : event.touches[0].clientY;
    const element: any = document.getElementById(id);
    const circle = document.createElement("span");
    circle.style.width = circle.style.height = `${size}px`;
    circle.style.left = `${clientX - size / 2}px`;
    circle.style.top = `${clientY - element.getBoundingClientRect().top - size / 2}px`;
    circle.classList.add("pl-ripple");
    const ripple = element.getElementsByClassName("pl-ripple")[0];
    if (ripple) ripple.remove();
    element.appendChild(circle);
};

export const utf8_to_b64 = (text: string) => {
    return window.btoa(unescape(encodeURIComponent(text)));
};

export const changeVideoUrl = (videoType?: string, videoId?: number) => {
    const { params } = store.getState().videoData;
    const recommendationVideo = findFirstVideoInRecommendationList();
    const type = videoType ? videoType : recommendationVideo.type;
    const id = videoId ? videoId : recommendationVideo.id;
    if (conditions(ConditionName.hasSportProvider)) {
        const queryParams = window.location.href.split("?")[1];
        if (!!queryParams) return `${window.location.origin}/ngpplayer/${type}/${id}?${queryParams}`;
        else return `${window.location.origin}/ngpplayer/${type}/${id}`;
    } else return window.location.href.replace(`type=${params.type}`, `type=${type}`).replace(`id=${params.id}`, `id=${id}`);
};

export const hasQuestionMark = (text: string) => (text.match(/\?/g) ? true : false);

export const responseValidator = (status: number): boolean => status >= 200 && status < 300;

export const generateNewAccessToken = async () => {
    if (authToken.get()?.refresh) {
        const { data, status } = await apiCaller({
            url: API().auth.refresh,
            method: "POST",
            data: { refresh: authToken.get()?.refresh }
        });
        if (responseValidator(status)) {
            const token = {
                access: data.access,
                refresh: authToken.get()?.refresh,
                token_id: data.token_id
            };
            authToken.set(token);
        } else {
            authToken.remove();
            throw new Error("");
        }
    }
};

export const convertB64ToFile = (dataUrl: string, filename: string) => {
    let arr = dataUrl.split(","),
        mime = arr[0].match(/:(.*?);/)![1],
        bstr = window.atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

// check if video Url is white or not for snapShot
export const isWhiteListUrl = (url: string) => !!whiteListTargets.find((t: string) => new URL(url).origin.includes(t));

export const parseJwt = (token: string) => {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split("")
            .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );
    return JSON.parse(jsonPayload);
};

export const handlePopoverSizeInMiniPlayer = () => {
    const { videoElementSizeInfo } = store.getState().videoData;

    return conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
        ? {
              top: videoElementSizeInfo.top,
              height: videoElementSizeInfo.height,
              left: videoElementSizeInfo.left
          }
        : {};
};

export const numbersToLetters = [
    "اول",
    "دوم",
    "سوم",
    "چهارم",
    "پنجم",
    "ششم",
    "هفتم",
    "هشتم",
    "تهم",
    "دهم",
    "یازدهم",
    "دوازدهم",
    "سیزدهم",
    "چهاردهم",
    "پانزدهم",
    "شانزدهم",
    "هفدهم",
    "هجدهم",
    "نوزدهم",
    "بیستم"
];

export const handleVideoType = () => {
    const { params } = store.getState().videoData;
    switch (params.type) {
        case "vod":
            return VideoNumberTypeEnum.Vod;
        case "archive":
        case "live":
            return VideoNumberTypeEnum.Archive;
        case "clip":
            return VideoNumberTypeEnum.Clip;
        default:
            return VideoNumberTypeEnum.FutureLive;
    }
};

export const handleHasXTokenParamOrSendXToken = () => {
    if (conditions(ConditionName.hasXTokenParamOrSendXToken)) return { "x-token": store.getState().videoData.params.xToken };
    else return undefined;
};

export function getWebEnvConfig(key: string): string {
    const webConfigs = store.getState().videoData.webListConfig;
    const config = webConfigs.find((item: any) => item.key === key);
    return config ? config.value : "";
}

export const getDomainFromUrl = (url: string) => {
    const splitedUrl = url.split(".");
    return `${splitedUrl[splitedUrl.length - 2]}.${splitedUrl[splitedUrl.length - 1]}`;
};

export const clearRedux = () => {
    const dispatch = store.dispatch;
    dispatch(setClearVideoDataSlice());
    dispatch(setClearControllerSlice());
    dispatch(setClearUserSlice());
    dispatch(setClearTagsSlice());
    dispatch(setClearPopoversSlice());
    dispatch(setClearCommentsSlice());
    dispatch(setClearChatSlice());
};

export const weighted_random = (options: any) => {
    let i;

    let weights: any[] = [];

    for (i = 0; i < options.length; i++) weights[i] = options[i].weight + (weights[i - 1] || 0);

    let random = Math.random() * weights[weights.length - 1];

    for (i = 0; i < weights.length; i++) if (weights[i] > random) break;

    return options[i].domain;
};

export const convertTimeToSeconds = (time: string) =>
    Number(time.split(":")[0]) * 3600 + Number(time.split(":")[1]) * 60 + Number(time.split(":")[2]);

export const imageFormatChecking = (filename: string) =>
    ["jpg", "png", "jpeg"].includes(filename.toLowerCase().split(".")[filename.toLowerCase().split(".").length - 1]);

// ***** Player Core *****

export const handlePageLoadTime = (loadingDurationRef: any) => {
    let pageLoadTime = 0;
    if (performance && performance.getEntriesByType("navigation")) pageLoadTime = performance.getEntriesByType("navigation")[0].duration;
    loadingDurationRef.current = pageLoadTime;
};

export const highestResolutionSupported = (resolutions: string[], width: number, height: number) => {
    const w = width * window.devicePixelRatio;
    const h = height * window.devicePixelRatio;
    let filteredResolutions: string[] = [];
    if (conditions(ConditionName.isTouchScreenPortrait))
        filteredResolutions = resolutions.filter((resolution: string) => Number(resolution.split("x")[0].trim()) <= w);
    else
        filteredResolutions = resolutions.filter(
            (resolution: string) => Number(resolution.split("x")[0].trim()) <= h || Number(resolution.split("x")[1].trim()) <= w
        );
    return filteredResolutions.indexOf(filteredResolutions[filteredResolutions.length - 1]);
};

export const getSubtitleLabel = (subtitleLanguage: string) => {
    let label;
    switch (subtitleLanguage) {
        case "fa":
            label = "فارسی";
            break;
        case "en":
            label = "انگلیسی";
            break;
        case "ar":
            label = "عربی";
            break;
        case "tr":
            label = "ترکی";
            break;
        default:
            break;
    }
    return label;
};

export const getOS = () => {
    const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
    const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
    const iosPlatforms = ["iPhone", "iPad", "iPod"];
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    let os = "";

    if (macosPlatforms.indexOf(platform) !== -1) os = "mac";
    else if (iosPlatforms.indexOf(platform) !== -1) os = "ios";
    else if (windowsPlatforms.indexOf(platform) !== -1) os = "windows";
    else if (/Android/.test(userAgent)) os = "android";
    else if (/Linux/.test(platform)) os = "linux";

    return os;
};

export const deleteQueryParamsInUrl = (queryParam: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(queryParam);
    history.pushState({}, "", url.href);
};

export const handleRedirectCards = (id?: string, type?: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("id", id!);
    url.searchParams.set("type", type!);
    return history.pushState({}, "", url.href);
};

export const strToObj = (str: string) => {
    var obj = {};
    if (str && typeof str === "string") {
        var objStr = str.match(/\{(.)+\}/g);
        eval("obj =" + objStr);
    }
    return obj;
};

////////////////////////////////////// start: check XSS attack for isGeneral Url state

export const hasPermissionToRedirect = (url: string) => {
    const dispatch = store.dispatch;
    dispatch(setIsFetchDetails(true));
    if (!!url) {
        try {
            const redirect: any = escapeInput(url);
            const redirectUrl = new URL(redirect);
            const redirectOriginUrl = new URL(url);
            if (!isWhiteListGeneralUrl(redirectOriginUrl.hostname, whiteListGeneralUrl)) {
                dispatch(setErrorType("404"));
                dispatch(setIsValidAddress(false));
            } else dispatch(setIsValidAddress(true));
        } catch (error) {
            dispatch(setErrorType("404"));
            dispatch(setIsValidAddress(false));
        }
    }
};

function escapeInput(toOutput: string | null) {
    if (toOutput === null) return null;
    return toOutput.replace(/\&/g, "&amp;").replace(/\</g, "&lt;").replace(/\>/g, "&gt;").replace(/\"/g, "&quot;").replace(/\'/g, "&#x27;");
}

function isWhiteListGeneralUrl(str: string, arrayOfStrings: string[]) {
    for (let i = 0; i < arrayOfStrings.length; i++) {
        if (str.startsWith(arrayOfStrings[i])) {
            return true;
        }
    }
    return false;
}

////////////////////////////////////// end: check XSS attack for isGeneral Url state

export const flattenObject = (input: any, keyName?: string) => {
    let result: any = {};
    for (const key in input) {
        const newKey = keyName ? `${keyName}_${key}` : key;
        if (typeof input[key] === "object" && !Array.isArray(input[key])) result = { ...result, ...flattenObject(input[key], newKey) };
        else result[newKey] = input[key];
    }
    return result;
};

export const findIntersectionBetweenTwoArray = (arr1: string[], arr2: string[]) => arr1.filter((element) => !arr2.includes(element));
